var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { getCardObstacle, getCardObstacleInParent } from './coords';
import { roundOrthogonalCorners, stringCurveFromOrthogonal } from './pathUtils';
import { getActiveDiagram } from './theme';
/** Default fillet radius for orthogonal / smart routes (px). 0 = sharp 90°. */
export var ROUTE_CORNER_RADIUS = 10;
/** Check if a horizontal segment intersects a rectangle */
function hSegIntersectsRect(x1, x2, y, obs) {
    var minX = Math.min(x1, x2);
    var maxX = Math.max(x1, x2);
    return y >= obs.y && y <= obs.y + obs.height && maxX >= obs.x && minX <= obs.x + obs.width;
}
/** Check if a vertical segment intersects a rectangle */
function vSegIntersectsRect(y1, y2, x, obs) {
    var minY = Math.min(y1, y2);
    var maxY = Math.max(y1, y2);
    return x >= obs.x && x <= obs.x + obs.width && maxY >= obs.y && minY <= obs.y + obs.height;
}
function pathHitsObstacles(points, obstacles) {
    var e_1, _a;
    for (var i = 0; i < points.length - 2; i += 2) {
        var x1 = points[i];
        var y1 = points[i + 1];
        var x2 = points[i + 2];
        var y2 = points[i + 3];
        try {
            for (var obstacles_1 = (e_1 = void 0, __values(obstacles)), obstacles_1_1 = obstacles_1.next(); !obstacles_1_1.done; obstacles_1_1 = obstacles_1.next()) {
                var obs = obstacles_1_1.value;
                if (x1 === x2) {
                    if (vSegIntersectsRect(y1, y2, x1, obs))
                        return true;
                }
                else if (y1 === y2) {
                    if (hSegIntersectsRect(x1, x2, y1, obs))
                        return true;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (obstacles_1_1 && !obstacles_1_1.done && (_a = obstacles_1.return)) _a.call(obstacles_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }
    return false;
}
/** Expand obstacles by padding for clearance */
function padObstacle(obs, pad) {
    return {
        x: obs.x - pad,
        y: obs.y - pad,
        width: obs.width + pad * 2,
        height: obs.height + pad * 2,
    };
}
/**
 * Flexible orthogonal route: prefers mid-span elbows that scale with distance
 * so wires bend smoothly as nodes are dragged apart.
 */
function smartOrthogonalRoute(x1, y1, x2, y2, obstacles, railOffset) {
    var e_2, _a;
    if (railOffset === void 0) { railOffset = 0; }
    var padded = obstacles.map(function (o) { return padObstacle(o, 10); });
    var dx = x2 - x1;
    var dy = y2 - y1;
    var absDx = Math.abs(dx);
    var absDy = Math.abs(dy);
    // Adaptive stub length — grows with separation for a cable-like bend
    var stubX = Math.min(72, Math.max(18, absDx * 0.28));
    var stubY = Math.min(72, Math.max(18, absDy * 0.28));
    var midX = (x1 + x2) / 2 + railOffset;
    var midY = (y1 + y2) / 2 + railOffset;
    var dirX = dx >= 0 ? 1 : -1;
    var dirY = dy >= 0 ? 1 : -1;
    var candidates = [
        // Classic mid-Y / mid-X buses
        [x1, y1, x1, midY, x2, midY, x2, y2],
        [x1, y1, midX, y1, midX, y2, x2, y2],
        // Exit horizontally then drop (cable elbow)
        [x1, y1, x1 + dirX * stubX, y1, x1 + dirX * stubX, y2, x2, y2],
        // Exit vertically then across
        [x1, y1, x1, y1 + dirY * stubY, x2, y1 + dirY * stubY, x2, y2],
        // Two-stub S-bend (HVH)
        [
            x1,
            y1,
            x1 + dirX * stubX,
            y1,
            x1 + dirX * stubX,
            midY,
            x2 - dirX * stubX,
            midY,
            x2 - dirX * stubX,
            y2,
            x2,
            y2,
        ],
        // Two-stub S-bend (VHV)
        [
            x1,
            y1,
            x1,
            y1 + dirY * stubY,
            midX,
            y1 + dirY * stubY,
            midX,
            y2 - dirY * stubY,
            x2,
            y2 - dirY * stubY,
            x2,
            y2,
        ],
        // Simple L shapes
        [x1, y1, x1, y2, x2, y2],
        [x1, y1, x2, y1, x2, y2],
    ];
    var best = null;
    var bestLen = Infinity;
    try {
        for (var candidates_1 = __values(candidates), candidates_1_1 = candidates_1.next(); !candidates_1_1.done; candidates_1_1 = candidates_1.next()) {
            var path = candidates_1_1.value;
            if (pathHitsObstacles(path, padded))
                continue;
            var len = 0;
            for (var i = 0; i < path.length - 2; i += 2) {
                len += Math.hypot(path[i + 2] - path[i], path[i + 3] - path[i + 1]);
            }
            // Prefer fewer corners when lengths are close
            var corners = path.length / 2 - 2;
            var score = len + corners * 12;
            if (score < bestLen) {
                bestLen = score;
                best = path;
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (candidates_1_1 && !candidates_1_1.done && (_a = candidates_1.return)) _a.call(candidates_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    if (best)
        return best;
    // Detour above or below all obstacles
    var minY = Math.min.apply(Math, __spreadArray([y1, y2], __read(padded.map(function (o) { return o.y; })), false)) - 36;
    var maxY = Math.max.apply(Math, __spreadArray([y1, y2], __read(padded.map(function (o) { return o.y + o.height; })), false)) + 36;
    var above = [x1, y1, x1, minY, x2, minY, x2, y2];
    var below = [x1, y1, x1, maxY, x2, maxY, x2, y2];
    if (!pathHitsObstacles(above, padded))
        return above;
    if (!pathHitsObstacles(below, padded))
        return below;
    return candidates[0];
}
/** Collect bounding boxes from node groups as routing obstacles (world space). */
export function collectObstacles(nodes, exclude) {
    var e_3, _a;
    var skip = new Set(exclude !== null && exclude !== void 0 ? exclude : []);
    var result = [];
    try {
        for (var nodes_1 = __values(nodes), nodes_1_1 = nodes_1.next(); !nodes_1_1.done; nodes_1_1 = nodes_1.next()) {
            var node = nodes_1_1.value;
            if (skip.has(node))
                continue;
            var card = getCardObstacle(node);
            if (card && card.width > 0 && card.height > 0) {
                result.push(card);
                continue;
            }
            var b = node.getBounds();
            if (b.width > 0 && b.height > 0) {
                result.push({ x: b.x, y: b.y, width: b.width, height: b.height });
            }
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (nodes_1_1 && !nodes_1_1.done && (_a = nodes_1.return)) _a.call(nodes_1);
        }
        finally { if (e_3) throw e_3.error; }
    }
    return result;
}
/**
 * Collect obstacles in a parent group's local space using live x/y (drag-safe).
 * Prefer this when routing connectors during interactive arrange.
 */
export function collectObstaclesInParent(nodes, parent, exclude) {
    var e_4, _a;
    var skip = new Set(exclude !== null && exclude !== void 0 ? exclude : []);
    var result = [];
    try {
        for (var nodes_2 = __values(nodes), nodes_2_1 = nodes_2.next(); !nodes_2_1.done; nodes_2_1 = nodes_2.next()) {
            var node = nodes_2_1.value;
            if (skip.has(node))
                continue;
            var box = getCardObstacleInParent(node, parent);
            if (box && box.width > 0 && box.height > 0) {
                result.push(box);
            }
        }
    }
    catch (e_4_1) { e_4 = { error: e_4_1 }; }
    finally {
        try {
            if (nodes_2_1 && !nodes_2_1.done && (_a = nodes_2.return)) _a.call(nodes_2);
        }
        finally { if (e_4) throw e_4.error; }
    }
    return result;
}
/** Compute routed polyline points between two anchors */
export function computeRoutePoints(x1, y1, x2, y2, style, obstacles, cornerRadius, 
/** Shift mid rails so parallel wires do not paint on top of each other. */
railOffset) {
    if (style === void 0) { style = 'orthogonal'; }
    if (obstacles === void 0) { obstacles = []; }
    if (cornerRadius === void 0) { cornerRadius = ROUTE_CORNER_RADIUS; }
    if (railOffset === void 0) { railOffset = 0; }
    // Near-colinear → straight segment (avoids zero-width elbows / kink artifacts)
    if (Math.abs(x1 - x2) < 1.5 || Math.abs(y1 - y2) < 1.5) {
        return [x1, y1, x2, y2];
    }
    if (style === 'straight') {
        return [x1, y1, x2, y2];
    }
    var points;
    if (style === 'smart' || (style === 'curved' && obstacles.length > 0)) {
        points = smartOrthogonalRoute(x1, y1, x2, y2, obstacles, railOffset);
    }
    else {
        // Prefer vertical-then-horizontal U when dy dominates (flowchart TB);
        // otherwise horizontal-then-vertical C (LR pipelines / class links).
        var absDx = Math.abs(x2 - x1);
        var absDy = Math.abs(y2 - y1);
        if (absDy >= absDx) {
            var midY = (y1 + y2) / 2 + railOffset;
            points = [x1, y1, x1, midY, x2, midY, x2, y2];
        }
        else {
            var midX = (x1 + x2) / 2 + railOffset;
            points = [x1, y1, midX, y1, midX, y2, x2, y2];
        }
    }
    points = collapseColinearPoints(points);
    if (points.length <= 4)
        return points;
    // Soft cable bends (explicit curved style)
    if (style === 'curved') {
        return stringCurveFromOrthogonal(points, 30);
    }
    // Orthogonal / smart: true 90° elbows; fillet when cornerRadius > 0
    if (cornerRadius > 0) {
        return roundOrthogonalCorners(points, cornerRadius, 12);
    }
    return points;
}
/** Drop zero-length / colinear intermediate vertices before filleting */
function collapseColinearPoints(points) {
    if (points.length < 6)
        return points;
    var out = [points[0], points[1]];
    for (var i = 2; i < points.length - 2; i += 2) {
        var x0 = out[out.length - 2];
        var y0 = out[out.length - 1];
        var x1 = points[i];
        var y1 = points[i + 1];
        var x2 = points[i + 2];
        var y2 = points[i + 3];
        var d01 = Math.hypot(x1 - x0, y1 - y0);
        var d12 = Math.hypot(x2 - x1, y2 - y1);
        if (d01 < 0.75 || d12 < 0.75)
            continue;
        var colinear = (Math.abs(x0 - x1) < 0.75 && Math.abs(x1 - x2) < 0.75) ||
            (Math.abs(y0 - y1) < 0.75 && Math.abs(y1 - y2) < 0.75);
        if (colinear)
            continue;
        out.push(x1, y1);
    }
    out.push(points[points.length - 2], points[points.length - 1]);
    return out;
}
/** Route connector between two points */
export function routeConnector(app, x1, y1, x2, y2, style, obstacles, stroke) {
    if (style === void 0) { style = 'orthogonal'; }
    if (obstacles === void 0) { obstacles = []; }
    if (stroke === void 0) { stroke = getActiveDiagram().edge; }
    var points = computeRoutePoints(x1, y1, x2, y2, style, obstacles);
    if (style === 'straight' && points.length === 4) {
        return app.line({
            x: x1,
            y: y1,
            x2: x2 - x1,
            y2: y2 - y1,
            stroke: stroke,
            strokeWidth: 2,
            lineCap: 'round',
        });
    }
    return app.polyline({
        points: points,
        fill: null,
        stroke: stroke,
        strokeWidth: 2,
        lineJoin: 'round',
        lineCap: 'round',
    });
}
/** Get anchor point on node edge toward target (ray–box intersection) */
export function getAnchor(node, targetX, targetY) {
    var b = node.getBounds();
    var cx = b.x + b.width / 2;
    var cy = b.y + b.height / 2;
    var dx = targetX - cx;
    var dy = targetY - cy;
    if (dx === 0 && dy === 0)
        return { x: cx, y: b.y };
    var hw = Math.max(b.width / 2, 1);
    var hh = Math.max(b.height / 2, 1);
    var scale = Math.max(Math.abs(dx) / hw, Math.abs(dy) / hh);
    return { x: cx + dx / scale, y: cy + dy / scale };
}
